import './Chat.css'

import { cx } from '../utilities/cx'
import { assistantMessageKey } from '../features/rheya/messageList'
import { ChatBubble } from './ChatBubble'
import { ChatInput } from './ChatInput'
import { ChatThread } from './ChatThread'
import { ToolBubble, ToolUseData } from './ToolBubble'

export type ChatMessage =
  | { role: 'user' | 'assistant'; content: string; messageId?: string }
  | { role: 'status'; toolUse: ToolUseData; messageId?: string }

export type ChatProps = {
  className?: string
  messages: ChatMessage[]
  inputValue: string
  inputPlaceholder?: string
  disabled?: boolean
  indicator?: React.ReactNode
  onInputChange: (value: string) => void
  onSubmit: () => void
  /** Hide assistant rows still held for TTS sync */
  heldKeys?: ReadonlySet<string>
  playingKey?: string | null
  onPlayMessage?: (text: string, key: string) => void
  showMic?: boolean
  recording?: boolean
  micBusy?: boolean
  levels?: number[]
  onMicPress?: () => void
  onCancelRecording?: () => void
  onSendRecording?: () => void
}

export const Chat = (props: ChatProps) => {
  const {
    className,
    messages,
    inputValue,
    inputPlaceholder,
    disabled,
    indicator,
    onInputChange,
    onSubmit,
    heldKeys,
    playingKey = null,
    onPlayMessage,
    showMic,
    recording,
    micBusy,
    levels,
    onMicPress,
    onCancelRecording,
    onSendRecording,
  } = props

  return (
    <div className={cx('chat', className)}>
      <ChatThread className="chat__thread">
        {messages.map((msg, idx) => {
          if (msg.role === 'status') {
            return <ToolBubble key={idx} toolUse={msg.toolUse} />
          }
          const body = msg.content?.trim() ?? ''
          if (!body) return null
          const audioKey =
            msg.role === 'assistant' ? assistantMessageKey(idx, body, msg.messageId) : undefined
          if (msg.role === 'assistant' && audioKey && heldKeys?.has(audioKey)) {
            return null
          }
          return (
            <ChatBubble
              key={msg.messageId ?? idx}
              sender={msg.role}
              {...(audioKey && onPlayMessage
                ? {
                    audioKey,
                    audioPlaying: playingKey === audioKey,
                    onAudioPress: () => onPlayMessage(body, audioKey),
                  }
                : {})}
            >
              {body}
            </ChatBubble>
          )
        })}
        {indicator}
      </ChatThread>
      <ChatInput
        className="chat__input"
        value={inputValue}
        {...(inputPlaceholder !== undefined && { placeholder: inputPlaceholder })}
        {...(disabled !== undefined && { disabled })}
        onChange={onInputChange}
        onSubmit={onSubmit}
        {...(showMic !== undefined && { showMic })}
        {...(recording !== undefined && { recording })}
        {...(micBusy !== undefined && { micBusy })}
        {...(levels !== undefined && { levels })}
        {...(onMicPress !== undefined && { onMicPress })}
        {...(onCancelRecording !== undefined && { onCancelRecording })}
        {...(onSendRecording !== undefined && { onSendRecording })}
      />
    </div>
  )
}
